/**
 * Cascading Page → Section → Action selector for an activity (plan 1904).
 * Options come from the backend registry tree. Section is disabled until a Page
 * is chosen, Action until a Section is chosen. Changing a parent clears its
 * children (so a stale Section/Action can't survive a Page change).
 */

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { RegistryTree } from "../api/activitiesAdminHooks";

interface ActivityTypeSelectProps {
  registry: RegistryTree | undefined;
  page: string;
  section: string;
  action: string;
  onChange: (next: { page: string; section: string; action: string }) => void;
  disabled?: boolean;
}

export function ActivityTypeSelect({
  registry,
  page,
  section,
  action,
  onChange,
  disabled,
}: ActivityTypeSelectProps) {
  const pages = registry?.pages ?? [];
  const sections = pages.find((p) => p.key === page)?.sections ?? [];
  const actions = sections.find((s) => s.key === section)?.actions ?? [];

  return (
    <div className="grid grid-cols-3 gap-2">
      <Select
        value={page}
        onValueChange={(v) => onChange({ page: v, section: "", action: "" })}
        disabled={disabled}
      >
        <SelectTrigger>
          <SelectValue placeholder="หน้า" />
        </SelectTrigger>
        <SelectContent>
          {pages.map((p) => (
            <SelectItem key={p.key} value={p.key}>
              {p.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={section}
        onValueChange={(v) => onChange({ page, section: v, action: "" })}
        disabled={disabled || !page}
      >
        <SelectTrigger>
          <SelectValue placeholder="ส่วน" />
        </SelectTrigger>
        <SelectContent>
          {sections.map((s) => (
            <SelectItem key={s.key} value={s.key}>
              {s.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={action}
        onValueChange={(v) => onChange({ page, section, action: v })}
        disabled={disabled || !section}
      >
        <SelectTrigger>
          <SelectValue placeholder="การกระทำ" />
        </SelectTrigger>
        <SelectContent>
          {actions.map((a) => (
            <SelectItem key={a.key} value={a.key}>
              {a.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
